import React, { useEffect, useState, useCallback } from "react";
import { api, imgUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Trash2, LogOut, Upload, Plus, X as XIcon } from "lucide-react";

const AnalyticsList = ({ title, rows, emptyText }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-[#D8D3CC] p-6 bg-[#F7F6F2]">
        <h3 className="font-serif-display text-xl text-[#1A1F1B] mb-3">{title}</h3>
        <p className="text-sm text-[#849383]">{emptyText}</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="border border-[#D8D3CC] p-6 bg-[#F7F6F2]">
      <h3 className="font-serif-display text-xl text-[#1A1F1B] mb-4">{title}</h3>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li key={i} className="text-sm">
            <div className="flex justify-between items-baseline gap-3">
              <span className="text-[#1A1F1B] truncate" title={r.value}>
                {r.value || "—"}
              </span>
              <span className="text-[#849383] tabular-nums whitespace-nowrap">
                {r.count.toLocaleString()}
              </span>
            </div>
            <div className="mt-1 h-1 bg-[#EFECE5] overflow-hidden">
              <div
                className="h-full bg-[#3B4A3F]"
                style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const emptyArt = {
  title: "",
  medium: "Mixed Media",
  year: new Date().getFullYear(),
  dimensions: "",
  description: "",
  image_url: "",
  featured: false,
};

const emptyProduct = {
  title: "",
  kind: "print",
  medium: "",
  dimensions: "",
  description: "",
  price: 0,
  image_url: "",
  in_stock: true,
};

const emptyJournal = {
  title: "",
  slug: "",
  excerpt: "",
  cover_image_url: "",
  body: "",
  gallery_urls: [],
  published: true,
};

const emptyInsta = {
  image_url: "",
  caption: "",
  post_url: "",
  order: 0,
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("joc_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("analytics");
  const [artworks, setArtworks] = useState([]);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [journal, setJournal] = useState([]);
  const [insta, setInsta] = useState([]);
  const [artForm, setArtForm] = useState(emptyArt);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [journalForm, setJournalForm] = useState(emptyJournal);
  const [instaForm, setInstaForm] = useState(emptyInsta);
  const [uploadingFor, setUploadingFor] = useState(null);
  const { settings, loaded: settingsLoaded, refresh: refreshSettings } = useSettings();
  const [siteForm, setSiteForm] = useState(null);
  const [savingSite, setSavingSite] = useState(false);

  useEffect(() => {
    if (settingsLoaded && settings && !siteForm) {
      setSiteForm({
        home_hero_image_url: settings.home_hero_image_url || "",
        home_hero_eyebrow: settings.home_hero_eyebrow || "",
        home_hero_tagline: settings.home_hero_tagline || "",
        home_hero_description: settings.home_hero_description || "",
        about_image_url: settings.about_image_url || "",
        about_intro_name: settings.about_intro_name || "",
        about_paragraphs: [...(settings.about_paragraphs || [])],
        about_based_in: settings.about_based_in || "",
        about_working_in: settings.about_working_in || "",
        contact_intro: settings.contact_intro || "",
        contact_email: settings.contact_email || "",
        contact_instagram: settings.contact_instagram || "",
        commission_intro: settings.commission_intro || "",
        commission_process_steps: [...(settings.commission_process_steps || [])],
        commission_prices: [...(settings.commission_prices || [])],
        commission_price_note: settings.commission_price_note || "",
        shop_enabled: !!settings.shop_enabled,
        notify_email: settings.notify_email || "",
      });
    }
  }, [settingsLoaded, settings, siteForm]);

  const refresh = useCallback(async () => {
    try {
      const [a, p, m, c, s, an, j, ig] = await Promise.all([
        api.get("/artworks"),
        api.get("/products"),
        api.get("/contact"),
        api.get("/commissions"),
        api.get("/subscribers"),
        api.get("/analytics/summary"),
        api.get("/journal", { params: { published_only: false } }),
        api.get("/insta-posts"),
      ]);
      setArtworks(a.data);
      setProducts(p.data);
      setMessages(m.data);
      setCommissions(c.data);
      setSubscribers(s.data);
      setAnalytics(an.data);
      setJournal(j.data);
      setInsta(ig.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("joc_token");
        setToken(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) refresh();
  }, [token, refresh]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("joc_token", res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back, Jewel.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("joc_token");
    setToken(null);
  };

  const onUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFor(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (target === "art") setArtForm((f) => ({ ...f, image_url: res.data.url }));
      else if (target === "prod") setProdForm((f) => ({ ...f, image_url: res.data.url }));
      else if (target === "home_hero") setSiteForm((f) => ({ ...f, home_hero_image_url: res.data.url }));
      else if (target === "about_image") setSiteForm((f) => ({ ...f, about_image_url: res.data.url }));
      else if (target === "journal_cover") setJournalForm((f) => ({ ...f, cover_image_url: res.data.url }));
      else if (target === "journal_gallery") setJournalForm((f) => ({ ...f, gallery_urls: [...(f.gallery_urls || []), res.data.url] }));
      else if (target === "insta") setInstaForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  const saveSite = async (e) => {
    e.preventDefault();
    setSavingSite(true);
    try {
      await api.put("/settings", siteForm);
      await refreshSettings();
      toast.success("Site settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSite(false);
    }
  };

  const updateParagraph = (i, value) => {
    setSiteForm((f) => {
      const next = [...f.about_paragraphs];
      next[i] = value;
      return { ...f, about_paragraphs: next };
    });
  };
  const addParagraph = () => {
    setSiteForm((f) => ({ ...f, about_paragraphs: [...f.about_paragraphs, ""] }));
  };
  const removeParagraph = (i) => {
    setSiteForm((f) => ({
      ...f,
      about_paragraphs: f.about_paragraphs.filter((_, idx) => idx !== i),
    }));
  };

  const addArtwork = async (e) => {
    e.preventDefault();
    try {
      await api.post("/artworks", { ...artForm, year: Number(artForm.year) });
      toast.success("Artwork added");
      setArtForm(emptyArt);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delArtwork = async (id) => {
    await api.delete(`/artworks/${id}`);
    toast.success("Removed");
    refresh();
  };

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...prodForm, price: Number(prodForm.price) });
      toast.success("Product added");
      setProdForm(emptyProduct);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delProduct = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Removed");
    refresh();
  };

  const delMessage = async (id) => {
    await api.delete(`/contact/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const delCommission = async (id) => {
    await api.delete(`/commissions/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const delSubscriber = async (id) => {
    await api.delete(`/subscribers/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const toggleProductSold = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { in_stock: !p.in_stock });
      toast.success(!p.in_stock ? "Marked available" : "Marked sold");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const addJournal = async (e) => {
    e.preventDefault();
    try {
      await api.post("/journal", journalForm);
      toast.success("Journal entry published");
      setJournalForm(emptyJournal);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delJournal = async (id) => {
    if (!window.confirm("Delete this journal entry?")) return;
    await api.delete(`/journal/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const removeJournalGallery = (idx) => {
    setJournalForm((f) => ({
      ...f,
      gallery_urls: (f.gallery_urls || []).filter((_, i) => i !== idx),
    }));
  };

  const addInsta = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insta-posts", instaForm);
      toast.success("Instagram post added");
      setInstaForm(emptyInsta);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delInsta = async (id) => {
    await api.delete(`/insta-posts/${id}`);
    toast.success("Removed");
    refresh();
  };

  const copyAllSubscribers = async () => {
    const emails = subscribers.map((s) => s.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`${subscribers.length} emails copied to clipboard`);
    } catch {
      toast.error("Could not copy. Select & copy manually.");
    }
  };

  const updatePrice = (idx, field, value) => {
    setSiteForm((f) => {
      const next = [...f.commission_prices];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, commission_prices: next };
    });
  };
  const addPrice = () => {
    setSiteForm((f) => ({
      ...f,
      commission_prices: [
        ...f.commission_prices,
        { size: "", price_text: "", enabled: true },
      ],
    }));
  };
  const removePrice = (idx) => {
    setSiteForm((f) => ({
      ...f,
      commission_prices: f.commission_prices.filter((_, i) => i !== idx),
    }));
  };
  const updateStep = (idx, value) => {
    setSiteForm((f) => {
      const next = [...f.commission_process_steps];
      next[idx] = value;
      return { ...f, commission_process_steps: next };
    });
  };
  const addStep = () => {
    setSiteForm((f) => ({
      ...f,
      commission_process_steps: [...f.commission_process_steps, ""],
    }));
  };
  const removeStep = (idx) => {
    setSiteForm((f) => ({
      ...f,
      commission_process_steps: f.commission_process_steps.filter((_, i) => i !== idx),
    }));
  };

  if (!token) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-24 max-w-md mx-auto" data-testid="page-admin-login">
        <div className="label-eyebrow mb-2">Studio</div>
        <h1 className="font-serif-display text-4xl text-[#1A1F1B] mb-8">
          Admin sign in
        </h1>
        <form onSubmit={login} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="label-eyebrow">Email</label>
            <input
              type="email"
              className="earth"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="label-eyebrow">Password</label>
            <input
              type="password"
              className="earth"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
            />
          </div>
          <button type="submit" className="btn-primary w-full" data-testid="admin-login-submit">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="page-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-eyebrow">Studio Admin</div>
          <h1 className="font-serif-display text-4xl text-[#1A1F1B]">
            Manage the gallery
          </h1>
        </div>
        <button onClick={logout} className="btn-secondary inline-flex items-center gap-2" data-testid="admin-logout">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="flex gap-2 mb-10 border-b border-[#D8D3CC] overflow-x-auto" data-testid="admin-tabs">
        {[
          { k: "analytics", l: "Analytics" },
          { k: "artworks", l: "Artworks" },
          { k: "products", l: "Shop Products" },
          { k: "journal", l: `Journal (${journal.length})` },
          { k: "insta", l: `Instagram (${insta.length})` },
          { k: "messages", l: `Messages (${messages.length})` },
          { k: "commissions", l: `Commissions (${commissions.length})` },
          { k: "subscribers", l: `Subscribers (${subscribers.length})` },
          { k: "settings", l: "Site Settings" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
              tab === t.k
                ? "border-[#3B4A3F] text-[#3B4A3F]"
                : "border-transparent text-[#849383] hover:text-[#3B4A3F]"
            }`}
            data-testid={`admin-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "analytics" && (
        <div className="space-y-10 max-w-5xl" data-testid="admin-analytics">
          <div>
            <h2 className="font-serif-display text-3xl text-[#1A1F1B]">
              Studio Analytics
            </h2>
            <p className="text-sm text-[#849383] mt-1">
              How visitors are exploring your studio. Refresh the page for the latest numbers.
            </p>
          </div>

          {!analytics || analytics.total_views === 0 ? (
            <div className="border border-[#D8D3CC] p-10 text-center bg-[#F7F6F2]" data-testid="admin-analytics-empty">
              <p className="text-[#849383]">
                No visits tracked yet. Once your site is shared and people start
                visiting, their stats will appear here.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Today", value: analytics.today_views },
                  { label: "This Week", value: analytics.week_views },
                  { label: "This Month", value: analytics.month_views },
                  { label: "All Time", value: analytics.total_views },
                ].map((s) => (
                  <div key={s.label} className="border border-[#D8D3CC] p-5 bg-[#F7F6F2]">
                    <div className="label-eyebrow">{s.label}</div>
                    <div className="font-serif-display text-4xl text-[#1A1F1B] mt-2">
                      {s.value.toLocaleString()}
                    </div>
                    <div className="text-xs text-[#849383] mt-1">page views</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="border border-[#D8D3CC] p-5 bg-[#F7F6F2] col-span-2">
                  <div className="label-eyebrow">Unique Visitors</div>
                  <div className="font-serif-display text-3xl text-[#1A1F1B] mt-2">
                    {analytics.total_unique_visitors.toLocaleString()}
                  </div>
                  <div className="text-xs text-[#849383] mt-1">
                    {analytics.week_unique_visitors.toLocaleString()} this week
                  </div>
                </div>
                <div className="border border-[#D8D3CC] p-5 bg-[#F7F6F2]">
                  <div className="label-eyebrow">Mobile</div>
                  <div className="font-serif-display text-3xl text-[#1A1F1B] mt-2">
                    {(analytics.devices?.mobile || 0).toLocaleString()}
                  </div>
                </div>
                <div className="border border-[#D8D3CC] p-5 bg-[#F7F6F2]">
                  <div className="label-eyebrow">Desktop</div>
                  <div className="font-serif-display text-3xl text-[#1A1F1B] mt-2">
                    {(analytics.devices?.desktop || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <AnalyticsList
                  title="Most viewed pages"
                  rows={analytics.top_pages}
                  emptyText="No page views yet."
                />
                <AnalyticsList
                  title="Top referrers"
                  rows={analytics.top_referrers}
                  emptyText="No referrer data yet — most visitors are coming directly."
                />
                <AnalyticsList
                  title="Countries"
                  rows={analytics.top_countries}
                  emptyText="Location data isn't available yet."
                />
                <AnalyticsList
                  title="Most viewed artworks"
                  rows={(analytics.top_artworks || []).map((a) => ({
                    value: `${a.title}${a.medium ? " · " + a.medium : ""}`,
                    count: a.count,
                  }))}
                  emptyText="No artwork views yet — when visitors click pieces in your portfolio, their views will show here."
                />
              </div>
            </>
          )}
        </div>
      )}


      {tab === "artworks" && (
        <div className="grid lg:grid-cols-12 gap-10">
          <form onSubmit={addArtwork} className="lg:col-span-5 space-y-5 border border-[#D8D3CC] p-6" data-testid="admin-artwork-form">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Add Artwork</h2>
            <input className="earth" placeholder="Title" required value={artForm.title} onChange={(e) => setArtForm({ ...artForm, title: e.target.value })} data-testid="admin-art-title" />
            <div className="grid grid-cols-2 gap-4">
              <select className="earth" value={artForm.medium} onChange={(e) => setArtForm({ ...artForm, medium: e.target.value })} data-testid="admin-art-medium">
                <option>Mixed Media</option>
                <option>Acrylic</option>
                <option>Oil</option>
                <option>Collage</option>
              </select>
              <input className="earth" type="number" placeholder="Year" value={artForm.year} onChange={(e) => setArtForm({ ...artForm, year: e.target.value })} data-testid="admin-art-year" />
            </div>
            <input className="earth" placeholder='Dimensions (e.g. 24" x 30")' value={artForm.dimensions} onChange={(e) => setArtForm({ ...artForm, dimensions: e.target.value })} data-testid="admin-art-dimensions" />
            <textarea className="earth" rows={3} placeholder="Description" value={artForm.description} onChange={(e) => setArtForm({ ...artForm, description: e.target.value })} data-testid="admin-art-description" />
            <div className="space-y-2">
              <label className="label-eyebrow">Image</label>
              <div className="flex items-center gap-3">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} />
                  {uploadingFor === "art" ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "art")} data-testid="admin-art-upload" />
                </label>
                <input className="earth flex-1" placeholder="or paste URL" value={artForm.image_url} onChange={(e) => setArtForm({ ...artForm, image_url: e.target.value })} data-testid="admin-art-imageurl" />
              </div>
              {artForm.image_url && (
                <div className="w-32 h-32 border border-[#D8D3CC] bg-[#EFECE5]">
                  <img src={imgUrl(artForm.image_url)} alt="preview" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-[#4E5C50]">
              <input type="checkbox" checked={artForm.featured} onChange={(e) => setArtForm({ ...artForm, featured: e.target.checked })} data-testid="admin-art-featured" /> Featured on home page
            </label>
            <button type="submit" className="btn-primary w-full" data-testid="admin-art-submit">Add Artwork</button>
          </form>

          <div className="lg:col-span-7 space-y-3" data-testid="admin-artworks-list">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">Existing ({artworks.length})</h2>
            {artworks.map((a) => (
              <div key={a.id} className="flex gap-4 items-center border border-[#D8D3CC] p-3" data-testid={`admin-artwork-row-${a.id}`}>
                <div className="w-16 h-16 bg-[#EFECE5] flex-shrink-0">
                  <img src={imgUrl(a.image_url)} alt={a.title} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1">
                  <div className="font-serif-display text-lg text-[#1A1F1B]">{a.title}</div>
                  <div className="text-xs text-[#849383]">{a.medium} · {a.year}{a.featured && " · ★ Featured"}</div>
                </div>
                <button onClick={() => delArtwork(a.id)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-artwork-delete-${a.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="grid lg:grid-cols-12 gap-10">
          <form onSubmit={addProduct} className="lg:col-span-5 space-y-5 border border-[#D8D3CC] p-6" data-testid="admin-product-form">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Add Product</h2>
            <input className="earth" placeholder="Title" required value={prodForm.title} onChange={(e) => setProdForm({ ...prodForm, title: e.target.value })} data-testid="admin-prod-title" />
            <div className="grid grid-cols-2 gap-4">
              <select className="earth" value={prodForm.kind} onChange={(e) => setProdForm({ ...prodForm, kind: e.target.value })} data-testid="admin-prod-kind">
                <option value="print">Print</option>
                <option value="original">Original</option>
              </select>
              <input className="earth" type="number" step="0.01" placeholder="Price USD" required value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} data-testid="admin-prod-price" />
            </div>
            <input className="earth" placeholder="Medium" value={prodForm.medium} onChange={(e) => setProdForm({ ...prodForm, medium: e.target.value })} data-testid="admin-prod-medium" />
            <input className="earth" placeholder="Dimensions" value={prodForm.dimensions} onChange={(e) => setProdForm({ ...prodForm, dimensions: e.target.value })} data-testid="admin-prod-dimensions" />
            <textarea className="earth" rows={3} placeholder="Description" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} data-testid="admin-prod-description" />
            <div className="space-y-2">
              <label className="label-eyebrow">Image</label>
              <div className="flex items-center gap-3">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} />
                  {uploadingFor === "prod" ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "prod")} data-testid="admin-prod-upload" />
                </label>
                <input className="earth flex-1" placeholder="or paste URL" value={prodForm.image_url} onChange={(e) => setProdForm({ ...prodForm, image_url: e.target.value })} data-testid="admin-prod-imageurl" />
              </div>
              {prodForm.image_url && (
                <div className="w-32 h-32 border border-[#D8D3CC] bg-[#EFECE5]">
                  <img src={imgUrl(prodForm.image_url)} alt="preview" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-[#4E5C50]">
              <input type="checkbox" checked={prodForm.in_stock} onChange={(e) => setProdForm({ ...prodForm, in_stock: e.target.checked })} data-testid="admin-prod-instock" /> In stock
            </label>
            <button type="submit" className="btn-primary w-full" data-testid="admin-prod-submit">Add Product</button>
          </form>

          <div className="lg:col-span-7 space-y-3" data-testid="admin-products-list">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">Existing ({products.length})</h2>
            {products.map((p) => (
              <div key={p.id} className="flex gap-4 items-center border border-[#D8D3CC] p-3" data-testid={`admin-product-row-${p.id}`}>
                <div className="w-16 h-16 bg-[#EFECE5] flex-shrink-0">
                  <img src={imgUrl(p.image_url)} alt={p.title} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg text-[#1A1F1B] truncate">{p.title}</div>
                  <div className="text-xs text-[#849383]">
                    {p.kind} · ${p.price.toFixed(2)}
                    {!p.in_stock && <span className="text-[#A39171]"> · Sold</span>}
                  </div>
                </div>
                <button
                  onClick={() => toggleProductSold(p)}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                    p.in_stock
                      ? "border-[#D8D3CC] text-[#4E5C50] hover:border-[#A39171] hover:text-[#A39171]"
                      : "border-[#A39171] text-[#A39171] hover:bg-[#A39171] hover:text-[#F7F6F2]"
                  }`}
                  data-testid={`admin-product-toggle-sold-${p.id}`}
                  title={p.in_stock ? "Mark as sold" : "Mark as available"}
                >
                  {p.in_stock ? "Mark Sold" : "Mark Available"}
                </button>
                <button onClick={() => delProduct(p.id)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-product-delete-${p.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "journal" && (
        <div className="grid lg:grid-cols-12 gap-10" data-testid="admin-journal-tab">
          <form onSubmit={addJournal} className="lg:col-span-5 space-y-5 border border-[#D8D3CC] p-6" data-testid="admin-journal-form">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B]">New Journal Entry</h2>
            <input className="earth" placeholder="Title" required value={journalForm.title} onChange={(e) => setJournalForm({ ...journalForm, title: e.target.value })} data-testid="admin-journal-title" />
            <input className="earth" placeholder='URL slug (auto if blank) e.g. "in-the-studio-this-week"' value={journalForm.slug} onChange={(e) => setJournalForm({ ...journalForm, slug: e.target.value })} data-testid="admin-journal-slug" />
            <textarea className="earth" rows={2} placeholder="Short excerpt shown on list page" value={journalForm.excerpt} onChange={(e) => setJournalForm({ ...journalForm, excerpt: e.target.value })} data-testid="admin-journal-excerpt" />

            <div className="space-y-2">
              <label className="label-eyebrow">Cover image</label>
              <div className="flex items-center gap-3">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} />
                  {uploadingFor === "journal_cover" ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "journal_cover")} data-testid="admin-journal-cover-upload" />
                </label>
                <input className="earth flex-1" placeholder="or paste URL" value={journalForm.cover_image_url} onChange={(e) => setJournalForm({ ...journalForm, cover_image_url: e.target.value })} data-testid="admin-journal-cover-url" />
              </div>
              {journalForm.cover_image_url && (
                <div className="w-32 h-32 border border-[#D8D3CC] bg-[#EFECE5]">
                  <img src={imgUrl(journalForm.cover_image_url)} alt="cover" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <textarea className="earth" rows={8} required placeholder="Write the entry… (blank line between paragraphs)" value={journalForm.body} onChange={(e) => setJournalForm({ ...journalForm, body: e.target.value })} data-testid="admin-journal-body" />

            <div className="space-y-2">
              <label className="label-eyebrow">Gallery images ({(journalForm.gallery_urls || []).length})</label>
              <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer w-fit">
                <Upload size={14} />
                {uploadingFor === "journal_gallery" ? "Uploading…" : "Add image"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "journal_gallery")} data-testid="admin-journal-gallery-upload" />
              </label>
              {(journalForm.gallery_urls || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {journalForm.gallery_urls.map((u, i) => (
                    <div key={u} className="relative w-20 h-20 border border-[#D8D3CC] bg-[#EFECE5]" data-testid={`admin-journal-gallery-${i}`}>
                      <img src={imgUrl(u)} alt="g" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeJournalGallery(i)} className="absolute -top-2 -right-2 p-1 bg-[#F7F6F2] border border-[#D8D3CC] text-[#849383] hover:text-red-700" aria-label="Remove">
                        <XIcon size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-[#4E5C50]">
              <input type="checkbox" checked={journalForm.published} onChange={(e) => setJournalForm({ ...journalForm, published: e.target.checked })} data-testid="admin-journal-published" /> Publish immediately
            </label>
            <button type="submit" className="btn-primary w-full" data-testid="admin-journal-submit">Publish Entry</button>
          </form>

          <div className="lg:col-span-7 space-y-3" data-testid="admin-journal-list">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">Existing ({journal.length})</h2>
            {journal.length === 0 && <p className="text-[#849383]">No journal entries yet.</p>}
            {journal.map((j) => (
              <div key={j.id} className="flex gap-4 items-center border border-[#D8D3CC] p-3" data-testid={`admin-journal-row-${j.id}`}>
                <div className="w-16 h-16 bg-[#EFECE5] flex-shrink-0">
                  <img src={imgUrl(j.cover_image_url)} alt={j.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg text-[#1A1F1B] truncate">{j.title}</div>
                  <div className="text-xs text-[#849383]">
                    /{j.slug} · {new Date(j.created_at).toLocaleDateString()}
                    {!j.published && <span className="text-[#A39171]"> · Draft</span>}
                  </div>
                </div>
                <button onClick={() => delJournal(j.id)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-journal-delete-${j.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "insta" && (
        <div className="grid lg:grid-cols-12 gap-10" data-testid="admin-insta-tab">
          <form onSubmit={addInsta} className="lg:col-span-5 space-y-5 border border-[#D8D3CC] p-6" data-testid="admin-insta-form">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Add Instagram Post</h2>
            <p className="text-xs text-[#849383] leading-relaxed">
              Upload the image you posted on Instagram, then paste the direct link to the post. Up to 6 will show on the home page.
            </p>
            <div className="space-y-2">
              <label className="label-eyebrow">Post image</label>
              <div className="flex items-center gap-3">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} />
                  {uploadingFor === "insta" ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "insta")} data-testid="admin-insta-upload" />
                </label>
                <input className="earth flex-1" placeholder="or paste URL" value={instaForm.image_url} onChange={(e) => setInstaForm({ ...instaForm, image_url: e.target.value })} data-testid="admin-insta-image-url" />
              </div>
              {instaForm.image_url && (
                <div className="w-32 h-32 border border-[#D8D3CC] bg-[#EFECE5]">
                  <img src={imgUrl(instaForm.image_url)} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <input className="earth" required placeholder="Instagram post URL (https://instagram.com/p/...)" value={instaForm.post_url} onChange={(e) => setInstaForm({ ...instaForm, post_url: e.target.value })} data-testid="admin-insta-post-url" />
            <input className="earth" placeholder="Caption (optional)" value={instaForm.caption} onChange={(e) => setInstaForm({ ...instaForm, caption: e.target.value })} data-testid="admin-insta-caption" />
            <div>
              <label className="label-eyebrow">Order (lower = shown first)</label>
              <input className="earth" type="number" value={instaForm.order} onChange={(e) => setInstaForm({ ...instaForm, order: Number(e.target.value) })} data-testid="admin-insta-order" />
            </div>
            <button type="submit" className="btn-primary w-full" data-testid="admin-insta-submit">Add Post</button>
          </form>

          <div className="lg:col-span-7" data-testid="admin-insta-list">
            <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">Existing ({insta.length})</h2>
            {insta.length === 0 && <p className="text-[#849383]">No Instagram posts yet.</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {insta.map((ig) => (
                <div key={ig.id} className="relative aspect-square bg-[#EFECE5] border border-[#D8D3CC] group" data-testid={`admin-insta-row-${ig.id}`}>
                  <img src={imgUrl(ig.image_url)} alt={ig.caption || "post"} className="w-full h-full object-cover" />
                  <button
                    onClick={() => delInsta(ig.id)}
                    className="absolute top-2 right-2 p-1.5 bg-[#F7F6F2]/95 border border-[#D8D3CC] text-[#849383] hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Delete"
                    data-testid={`admin-insta-delete-${ig.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A1F1B]/80 to-transparent p-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#F7F6F2]/90 truncate">
                      #{ig.order} · {ig.caption || "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-4" data-testid="admin-messages-list">
          <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">Inbox</h2>
          {messages.length === 0 ? (
            <p className="text-[#849383]">No messages yet.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className="border border-[#D8D3CC] p-5" data-testid={`admin-message-${m.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-serif-display text-xl text-[#1A1F1B]">{m.name}</div>
                    <div className="text-xs text-[#849383]">{m.email} · {new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => delMessage(m.id)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-message-delete-${m.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
                {m.subject && (
                  <div className="label-eyebrow mb-2">{m.subject}</div>
                )}
                <p className="text-sm text-[#4E5C50] leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))
          )}
        </div>
      )}
      {tab === "commissions" && (
        <div className="space-y-4" data-testid="admin-commissions-list">
          <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-4">
            Commission Requests
          </h2>
          {commissions.length === 0 ? (
            <p className="text-[#849383]">No commission requests yet.</p>
          ) : (
            commissions.map((c) => (
              <div key={c.id} className="border border-[#D8D3CC] p-5 bg-[#F7F6F2]" data-testid={`admin-commission-${c.id}`}>
                <div className="flex justify-between items-start mb-4 gap-4">
                  <div>
                    <div className="font-serif-display text-xl text-[#1A1F1B]">
                      {c.name}
                    </div>
                    <div className="text-xs text-[#849383]">
                      <a href={`mailto:${c.email}`} className="hover:text-[#3B4A3F]">
                        {c.email}
                      </a>{" "}
                      · {new Date(c.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button onClick={() => delCommission(c.id)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-commission-delete-${c.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 text-sm mb-4">
                  <div>
                    <div className="label-eyebrow">Size</div>
                    <div className="text-[#1A1F1B] mt-1">
                      {c.size}
                      {c.other_size_note && (
                        <span className="text-[#849383]"> — {c.other_size_note}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Needed by</div>
                    <div className="text-[#1A1F1B] mt-1">{c.needed_by || "—"}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Location</div>
                    <div className="text-[#1A1F1B] mt-1">{c.location || "US (default)"}</div>
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Vision</div>
                  <p className="text-sm text-[#4E5C50] leading-relaxed whitespace-pre-wrap">
                    {c.description}
                  </p>
                </div>
                {c.reference_image_urls && c.reference_image_urls.length > 0 && (
                  <div className="mt-4">
                    <div className="label-eyebrow mb-2">
                      Reference Photos ({c.reference_image_urls.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.reference_image_urls.map((url, i) => (
                        <a
                          key={url}
                          href={imgUrl(url)}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-20 h-20 bg-[#EFECE5] border border-[#D8D3CC]"
                          data-testid={`admin-commission-ref-${c.id}-${i}`}
                        >
                          <img src={imgUrl(url)} alt="ref" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "subscribers" && (
        <div className="space-y-4 max-w-3xl" data-testid="admin-subscribers-list">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">
                Newsletter Subscribers
              </h2>
              <p className="text-sm text-[#849383] mt-1">
                {subscribers.length === 0
                  ? "No one has subscribed yet."
                  : `${subscribers.length} ${subscribers.length === 1 ? "person has" : "people have"} signed up.`}
              </p>
            </div>
            {subscribers.length > 0 && (
              <button
                type="button"
                onClick={copyAllSubscribers}
                className="btn-secondary"
                data-testid="admin-subscribers-copy-all"
              >
                Copy All Emails
              </button>
            )}
          </div>
          {subscribers.length > 0 && (
            <div className="border border-[#D8D3CC] divide-y divide-[#D8D3CC] bg-[#F7F6F2]">
              {subscribers.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-4 px-5 py-3"
                  data-testid={`admin-subscriber-${s.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[#1A1F1B] truncate">{s.email}</div>
                    <div className="text-xs text-[#849383] mt-0.5">
                      {new Date(s.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                      {s.source && <> · via {s.source}</>}
                    </div>
                  </div>
                  <button
                    onClick={() => delSubscriber(s.id)}
                    className="p-2 text-[#849383] hover:text-red-700"
                    aria-label="Remove subscriber"
                    data-testid={`admin-subscriber-delete-${s.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "settings" && siteForm && (
        <form onSubmit={saveSite} className="grid lg:grid-cols-12 gap-10 max-w-5xl" data-testid="admin-settings-form">
          <div className="lg:col-span-8 space-y-12">
            {/* Shop toggle */}
            <section className="border border-[#D8D3CC] p-6 bg-[#EFECE5]">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B] mb-2">Shop visibility</h2>
              <p className="text-sm text-[#4E5C50] mb-4">
                Turn the shop off and the Shop nav link, cart, and home shop preview disappear from the site. Existing products are kept for when you turn it back on.
              </p>
              <label className="flex items-center gap-3 text-base text-[#1A1F1B]">
                <input
                  type="checkbox"
                  checked={siteForm.shop_enabled}
                  onChange={(e) => setSiteForm({ ...siteForm, shop_enabled: e.target.checked })}
                  data-testid="admin-settings-shop-enabled"
                />
                Shop is visible on the site
              </label>
            </section>

            {/* Home hero */}
            <section className="space-y-5">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Home page — Hero</h2>
              <div className="space-y-2">
                <label className="label-eyebrow">Hero photo (right side of home)</label>
                <div className="flex items-center gap-3">
                  <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                    <Upload size={14} />
                    {uploadingFor === "home_hero" ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "home_hero")} data-testid="admin-settings-hero-upload" />
                  </label>
                  <input className="earth flex-1" placeholder="or paste URL" value={siteForm.home_hero_image_url} onChange={(e) => setSiteForm({ ...siteForm, home_hero_image_url: e.target.value })} data-testid="admin-settings-hero-url" />
                </div>
                {siteForm.home_hero_image_url && (
                  <div className="w-40 h-48 bg-[#EFECE5] border border-[#D8D3CC]">
                    <img src={imgUrl(siteForm.home_hero_image_url)} alt="hero" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div>
                <label className="label-eyebrow">Eyebrow (small text above tagline)</label>
                <input className="earth" value={siteForm.home_hero_eyebrow} onChange={(e) => setSiteForm({ ...siteForm, home_hero_eyebrow: e.target.value })} data-testid="admin-settings-hero-eyebrow" />
              </div>
              <div>
                <label className="label-eyebrow">Tagline (main headline)</label>
                <input className="earth" value={siteForm.home_hero_tagline} onChange={(e) => setSiteForm({ ...siteForm, home_hero_tagline: e.target.value })} data-testid="admin-settings-hero-tagline" />
              </div>
              <div>
                <label className="label-eyebrow">Description</label>
                <textarea className="earth" rows={3} value={siteForm.home_hero_description} onChange={(e) => setSiteForm({ ...siteForm, home_hero_description: e.target.value })} data-testid="admin-settings-hero-description" />
              </div>
            </section>

            {/* About */}
            <section className="space-y-5">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">About page</h2>
              <div className="space-y-2">
                <label className="label-eyebrow">Portrait / studio photo</label>
                <div className="flex items-center gap-3">
                  <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                    <Upload size={14} />
                    {uploadingFor === "about_image" ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "about_image")} data-testid="admin-settings-about-upload" />
                  </label>
                  <input className="earth flex-1" placeholder="or paste URL" value={siteForm.about_image_url} onChange={(e) => setSiteForm({ ...siteForm, about_image_url: e.target.value })} data-testid="admin-settings-about-url" />
                </div>
                {siteForm.about_image_url && (
                  <div className="w-40 h-48 bg-[#EFECE5] border border-[#D8D3CC]">
                    <img src={imgUrl(siteForm.about_image_url)} alt="about" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div>
                <label className="label-eyebrow">First name (shown in &quot;Hello, I&apos;m ___.&quot;)</label>
                <input className="earth" value={siteForm.about_intro_name} onChange={(e) => setSiteForm({ ...siteForm, about_intro_name: e.target.value })} data-testid="admin-settings-about-name" />
              </div>
              <div>
                <label className="label-eyebrow">Bio paragraphs</label>
                <div className="space-y-3 mt-2" data-testid="admin-settings-paragraphs">
                  {siteForm.about_paragraphs.map((p, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <textarea
                        className="earth flex-1"
                        rows={3}
                        value={p}
                        onChange={(e) => updateParagraph(i, e.target.value)}
                        data-testid={`admin-settings-paragraph-${i}`}
                      />
                      <button type="button" onClick={() => removeParagraph(i)} className="p-2 text-[#849383] hover:text-red-700" data-testid={`admin-settings-paragraph-remove-${i}`} aria-label="Remove paragraph">
                        <XIcon size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addParagraph}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3B4A3F] hover:text-[#A39171]"
                    data-testid="admin-settings-paragraph-add"
                  >
                    <Plus size={14} /> Add paragraph
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-eyebrow">Based in</label>
                  <input className="earth" value={siteForm.about_based_in} onChange={(e) => setSiteForm({ ...siteForm, about_based_in: e.target.value })} data-testid="admin-settings-based-in" />
                </div>
                <div>
                  <label className="label-eyebrow">Working in</label>
                  <input className="earth" value={siteForm.about_working_in} onChange={(e) => setSiteForm({ ...siteForm, about_working_in: e.target.value })} data-testid="admin-settings-working-in" />
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="space-y-5">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Contact page</h2>
              <div>
                <label className="label-eyebrow">Intro paragraph</label>
                <textarea className="earth" rows={3} value={siteForm.contact_intro} onChange={(e) => setSiteForm({ ...siteForm, contact_intro: e.target.value })} data-testid="admin-settings-contact-intro" />
              </div>
              <div>
                <label className="label-eyebrow">Public email</label>
                <input className="earth" type="email" value={siteForm.contact_email} onChange={(e) => setSiteForm({ ...siteForm, contact_email: e.target.value })} data-testid="admin-settings-contact-email" />
              </div>
              <div>
                <label className="label-eyebrow">Instagram handle (without @)</label>
                <input className="earth" value={siteForm.contact_instagram} onChange={(e) => setSiteForm({ ...siteForm, contact_instagram: e.target.value })} data-testid="admin-settings-contact-instagram" />
              </div>
            </section>

            {/* Notifications */}
            <section className="space-y-5 border border-[#D8D3CC] p-6 bg-[#EFECE5]">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Email notifications</h2>
              <p className="text-sm text-[#4E5C50]">
                Where should new contact messages and commission requests be forwarded? Leave blank to disable email forwarding (they'll still show up in the admin tabs).
              </p>
              <div>
                <label className="label-eyebrow">Notify this email address</label>
                <input
                  className="earth"
                  type="email"
                  placeholder="you@yourinbox.com"
                  value={siteForm.notify_email}
                  onChange={(e) => setSiteForm({ ...siteForm, notify_email: e.target.value })}
                  data-testid="admin-settings-notify-email"
                />
              </div>
            </section>

            {/* Commissions */}
            <section className="space-y-5">
              <h2 className="font-serif-display text-2xl text-[#1A1F1B]">Commissions page</h2>
              <div>
                <label className="label-eyebrow">Intro paragraph</label>
                <textarea className="earth" rows={3} value={siteForm.commission_intro} onChange={(e) => setSiteForm({ ...siteForm, commission_intro: e.target.value })} data-testid="admin-settings-commission-intro" />
              </div>
              <div>
                <label className="label-eyebrow">Note shown next to pricing</label>
                <textarea className="earth" rows={3} value={siteForm.commission_price_note} onChange={(e) => setSiteForm({ ...siteForm, commission_price_note: e.target.value })} data-testid="admin-settings-commission-price-note" />
              </div>
              <div>
                <label className="label-eyebrow">Sizes & Pricing</label>
                <div className="space-y-2 mt-3" data-testid="admin-settings-prices">
                  {siteForm.commission_prices.map((p, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`admin-settings-price-${i}`}>
                      <input
                        className="earth flex-1"
                        placeholder='Size (e.g. 8x10)'
                        value={p.size}
                        onChange={(e) => updatePrice(i, "size", e.target.value)}
                        data-testid={`admin-settings-price-size-${i}`}
                      />
                      <input
                        className="earth flex-1"
                        placeholder='Price (e.g. $250)'
                        value={p.price_text}
                        onChange={(e) => updatePrice(i, "price_text", e.target.value)}
                        data-testid={`admin-settings-price-text-${i}`}
                      />
                      <label className="flex items-center gap-2 text-xs text-[#4E5C50] whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={p.enabled !== false}
                          onChange={(e) => updatePrice(i, "enabled", e.target.checked)}
                          data-testid={`admin-settings-price-enabled-${i}`}
                        />
                        Show
                      </label>
                      <button
                        type="button"
                        onClick={() => removePrice(i)}
                        className="p-2 text-[#849383] hover:text-red-700"
                        aria-label="Remove"
                        data-testid={`admin-settings-price-remove-${i}`}
                      >
                        <XIcon size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addPrice}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3B4A3F] hover:text-[#A39171] mt-2"
                    data-testid="admin-settings-price-add"
                  >
                    <Plus size={14} /> Add size
                  </button>
                </div>
              </div>
              <div>
                <label className="label-eyebrow">Process steps</label>
                <div className="space-y-3 mt-3" data-testid="admin-settings-steps">
                  {siteForm.commission_process_steps.map((s, i) => (
                    <div key={i} className="flex gap-2 items-start" data-testid={`admin-settings-step-${i}`}>
                      <div className="font-serif-display text-2xl text-[#3B4A3F] w-8 flex-shrink-0 pt-2">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <textarea
                        className="earth flex-1"
                        rows={2}
                        value={s}
                        onChange={(e) => updateStep(i, e.target.value)}
                        data-testid={`admin-settings-step-text-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeStep(i)}
                        className="p-2 text-[#849383] hover:text-red-700"
                        aria-label="Remove step"
                        data-testid={`admin-settings-step-remove-${i}`}
                      >
                        <XIcon size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addStep}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3B4A3F] hover:text-[#A39171]"
                    data-testid="admin-settings-step-add"
                  >
                    <Plus size={14} /> Add step
                  </button>
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-24 border border-[#D8D3CC] p-6 bg-[#F7F6F2] space-y-4">
              <h3 className="font-serif-display text-xl text-[#1A1F1B]">Save changes</h3>
              <p className="text-xs text-[#849383] leading-relaxed">
                Changes go live on your website the moment you click save.
              </p>
              <button type="submit" disabled={savingSite} className="btn-primary w-full disabled:opacity-50" data-testid="admin-settings-save">
                {savingSite ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Admin;
